import {
	Bullseye,
	Button,
	Content,
	ContentVariants,
	Popover,
	Skeleton,
	Stack,
	StackItem,
} from "@patternfly/react-core";
import { CheckCircleIcon, OutlinedQuestionCircleIcon } from "@patternfly/react-icons";
import { TableText } from "@patternfly/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";
import { findLowGradeImagesByOrgId } from "../../../api/pyxis";
import SoftwareCodeIcon from "../../../assets/software-code-red.svg";
import type { IPartnershipStatus } from "../../../constants/apiconstants-pfs";
import type { DataEntity, FreshnessGradesEntity, ITotalImagesCount } from "../../../constants/apiconstants-pyxis";
import type { DragHandleProps } from "../../../constants/CardsStructure";
import Env from "../../../utils/env";
import { getOrgID } from "../../../utils/fetch";
import { PFTableV2, Table } from "../../general/Table";
import type { IColumn } from "../../general/Table/PFTableV2";
import { DashboardCard } from "../shared/DashboardCard";

const getCurrentActiveFreshnessGrade = (
	grades: FreshnessGradesEntity[] | null | undefined,
): FreshnessGradesEntity | undefined =>
	grades?.find(
		(grade) =>
			new Date() > new Date(grade.start_date) && (grade.end_date ? new Date() < new Date(grade.end_date) : true),
	);

export const LowGradeImagesCard: FC<{ dragHandleProps?: DragHandleProps | undefined }> = ({ dragHandleProps }) => {
	const queryClient = useQueryClient();
	const partnershipStatus = queryClient.getQueryState(["partnershipStatus"])?.data as IPartnershipStatus;
	const orgId = getOrgID();
	const totalImageCount = queryClient.getQueryState(["totalImageCount"])?.data as ITotalImagesCount | undefined;
	const paginationObject = { page: 1, page_size: 150 };
	const sortObject = { field: "freshness_grades.creation_date", order: "DESC" };
	const filterObject = {
		and: [
			{ org_id: { eq: parseInt(orgId, 10) } },
			{
				or: [
					{ freshness_grades: { start_date: { lt: new Date().toISOString() } } },
					{ freshness_grades: { end_date: { gt: new Date().toISOString() } } },
					{ freshness_grades: { end_date: { eq: null } } },
				],
			},
			{ freshness_grades: { grade: { ne: "A" } } },
			{ deleted: { eq: false } },
			{ certified: { eq: true } },
		],
	};

	const { data: imageList, isFetching } = useQuery({
		queryKey: ["images"],
		queryFn: async () => {
			const response = await findLowGradeImagesByOrgId(paginationObject, filterObject, sortObject);
			const uniqueComponentsSet = new Set<string>();
			const uniqueImagesArray: DataEntity[] = [];
			response?.data?.lowGradeImagesList?.data?.forEach((item) => {
				const id = item?.edges?.cert_project?.data?._id;
				const projectStatus = item?.edges?.cert_project?.data?.project_status;
				const attachedProductListings = item?.edges?.cert_project?.data?.edges?.product_listings?.data || [];
				const isImageValidForLowGrade =
					!!id &&
					!uniqueComponentsSet.has(id) &&
					projectStatus !== "archived" &&
					attachedProductListings?.length > 0 &&
					uniqueImagesArray?.length < 5;
				if (isImageValidForLowGrade) {
					uniqueComponentsSet.add(id);
					uniqueImagesArray.push(item);
				}
			});
			return uniqueImagesArray;
		},
		enabled: !!(totalImageCount && totalImageCount?.data?.totalImagesCountByOrgId?.total > 0),
	});

	const HealthIndexTitle = () => (
		<>
			Health index&nbsp;
			<Popover
				triggerAction="hover"
				aria-label="Hoverable popover"
				headerContent="Container health index"
				bodyContent={
					<Content>
						<Content component="p">
							Container Health Index is a measure of the oldest and most severe security updates available for an image.
							&lsquo;A&rsquo; is more up to date than &lsquo;F&rsquo;.
						</Content>
					</Content>
				}
				footerContent={
					<Button
						component="a"
						isInline
						variant="link"
						href="https://redhat-connect.gitbook.io/catalog-help/about-container-images/understanding-container-security/container-health"
						target="_blank"
					>
						Learn More
					</Button>
				}
			>
				<OutlinedQuestionCircleIcon color="#666" />
			</Popover>
		</>
	);

	const columns: IColumn[] = [
		{
			title: "Component",
			key: "component",
			sortable: false,
			cell: (data) => (
				<TableText wrapModifier="truncate">
					<a href={`${Env.ConnectPortalUrls[Env.getEnvName()]}component/view/${data?.edges?.cert_project?.data?._id}`}>
						{data?.edges?.cert_project?.data?.name}
					</a>
				</TableText>
			),
			accessor: (data) => data?.edges?.cert_project?.data?.name,
			extraProps: { column: { width: 40 }, row: { className: "pf-m-break-word" } },
		},
		{
			title: "Products affected",
			sortable: false,
			key: "products",
			cell: (data) => (
				<TableText wrapModifier="truncate">
					<a
						href={`${Env.ConnectPortalUrls[Env.getEnvName()]}manage/products/${data?.edges?.cert_project?.data?.edges?.product_listings?.data?.[0]?._id}/overview`}
					>
						{data?.edges?.cert_project?.data?.edges?.product_listings?.data?.[0]?.name}
					</a>
				</TableText>
			),
			accessor: (data) => data?.edges?.cert_project?.data?.edges?.product_listings?.data?.[0]?.name ?? "",
			extraProps: { column: { width: 40 }, row: { className: "pf-m-break-word" } },
		},
		{
			title: <HealthIndexTitle />,
			sortable: false,
			colText: "Health index",
			key: "health-index",
			cell: (data) => {
				const grade = getCurrentActiveFreshnessGrade(data?.freshness_grades)?.grade;
				return (
					<TableText>
						{grade ? (
							<pfe-health-index health-index={grade} size="mini">
								{grade}
							</pfe-health-index>
						) : (
							"Unknown"
						)}
					</TableText>
				);
			},
			accessor: (data) => getCurrentActiveFreshnessGrade(data?.freshness_grades)?.grade ?? "NA",
			extraProps: { column: { width: 20 }, row: { className: "pf-m-break-word" } },
		},
	];

	const LowGradeImagesCardBody = () => {
		if (isFetching) {
			return (
				<Stack hasGutter>
					{[...Array(6)].map((_, i) => (
						<StackItem key={i}>
							<Skeleton height="100%" />
						</StackItem>
					))}
				</Stack>
			);
		}
		if (imageList?.length === 0) {
			return (
				<Bullseye>
					<Content>
						<Content component={ContentVariants.p}>
							<CheckCircleIcon style={{ color: "#3E8635" }} /> &nbsp; No components currently have images with grades
							that need improvement.
						</Content>
					</Content>
				</Bullseye>
			);
		}
		return (
			<Table columns={columns} rows={imageList ?? []}>
				<PFTableV2 variant="compact" aria="Products with low grade images" />
			</Table>
		);
	};

	if (totalImageCount?.data?.totalImagesCountByOrgId?.total === 0) return null;
	if (!partnershipStatus?.buildPartner) return null;

	return (
		<DashboardCard
			icon={SoftwareCodeIcon}
			title="Products with low grade images"
			footerLinks={[
				{ label: "View all components", href: `${Env.ConnectPortalUrls[Env.getEnvName()]}manage/components` },
			]}
			cardBody={
				<div style={{ minHeight: "16em" }}>
					<LowGradeImagesCardBody />
				</div>
			}
			dragHandleProps={dragHandleProps}
		/>
	);
};
